<script setup>
const { setupGridFilters } = useCourseData();
const {searchLocationFilter} = useLocationData();
const props = defineProps({
  options: Object,
  total: Number
})
const showModal = ref(false)
const leadGenModalTrigger = ref(false) // <-- Add this flag to the store
const emit = defineEmits(['onFiltersChanged'])
const filtersUnordered = []
const config = useRuntimeConfig()

if (props.options.ageFilter && props.options.ageFilter.display) {
  const filter = setupGridFilters('ages', props.options.ageFilter)
  filtersUnordered.push(filter)
}

if (props.options.interestFilter && props.options.interestFilter.display) {
  const filter = setupGridFilters('interests', props.options.interestFilter)
  filtersUnordered.push(filter)
}

if (props.options.programsFilter !== null && props.options.programsFilter.display) {
  const filter = setupGridFilters('programs', props.options.programsFilter)
  filtersUnordered.push(filter)
}

if (props.options.formatFilter && props.options.formatFilter.display) {
  const filter = setupGridFilters('format', props.options.formatFilter)
  filtersUnordered.push(filter)
}

if (props.options.locationFilter && props.options.locationFilter.display) {
  const filter = {
    id: 'location',    
    placeholder: props.options.locationFilter.placeholder,
    sortOrder: props.options.locationFilter.sortOrder
  }
  filtersUnordered.push(filter)
}

const filters = filtersUnordered.sort((a, b) => {
  return a.sortOrder - b.sortOrder;
})

const missing = 5 - filters.length;
const showLocation = ref(true), locationCatalog = ref(props.options.locationFilter && props.options.locationFilter.display)
const filtersRef = ref(), locationFilter = ref()

const handleFilterSelection = (field) => {
  
  if (field.id === 'format') {
    if (field.value === 'Online') {
      showLocation.value = false;
    }
    else {
      showLocation.value = true
    }
  }

  emit('onFiltersChanged', {
    name: field.id,
    value: field.value
  })
}

const handleLocationSearch = async (searchQuery) => {
  const {data: locations, error} = await searchLocationFilter(searchQuery); 
  
  if (locations.value) {
    const locationIds = locations.value.data.map((location) => {
      return location.pulseLocation.pulseId;
    })

    emit('onFiltersChanged', {
      name: 'location',    
      searchText: searchQuery,
      value: locationIds
    })
  }
}

const handleFocusout = (searchQuery) => {
  if (searchQuery === '') {
    emit('onFiltersChanged', {
      name: 'location',
      searchText: searchQuery,
      value: ''
    })
  }
}

const handleClearFilters = () => {
  const filterNames = ['ages', 'interests', 'programs', 'format', 'location']
  filtersRef.value.forEach(element => {
    element.reset()
  });

  showLocation.value = true

  if (locationFilter.value && Array.isArray(locationFilter.value) && locationFilter.value.length > 0) {    
    locationFilter.value[0].setSearchText('')
  }
  
  filterNames.forEach((name) => {
    let value = ['All']
    if (name === 'location') {
      value = ''
    }
    else if (name === 'format') {
      value = 'All'
    }

    emit('onFiltersChanged', {
      name: name,
      value: value
    })
  })
}
const route = useRoute();

const applyLocationFilterFromQuery = async () => {
  const locationQuery = route.query.location;
  if (locationQuery) {
    await handleLocationSearch(locationQuery);
  }
};


const sticky = ref(false)
const observer = ref({});

const observeCourseBody = () => {
  const aspectRatio = screen.width / screen.height;
  const threshold = aspectRatio >= 1.82 ? 0.7 : (aspectRatio >= 1.7 ? 0.2 : 0);
  if (config.public.nuxtEnvironment !== 'production') {
    console.log('Aspect Ratio: ', aspectRatio);
    console.log('Threshold: ', threshold);
  }    
  // const threshold = screen.width >= 1200 ? 0.7 : 0;  
  
  observer.value = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {      
      sticky.value = !entry.isIntersecting;      
    })
  }, {threshold: threshold});
  
  const courseBody = document.getElementById('courses-body');
  
  if (courseBody) {
    observer.value.observe(courseBody);
  }
  else {
    sticky.value = true;
  }

  window.addEventListener("resize", (event) => {
    observer.value.threshold = screen.width >= 1200 ? 0.5 : 0;
  });

  
}

onUpdated(() => {
  observeCourseBody();
});

onMounted(async () => {
  if (props.options.locationFilter && props.options.locationFilter.selected) {
    await handleLocationSearch(props.options.locationFilter.selected);
  }
  await applyLocationFilterFromQuery();
  observeCourseBody();
});

watch(route, async () => {
  await applyLocationFilterFromQuery();
});

const panelClass = computed(() => {
  return {
    'sticky': sticky.value
  }
})

</script>

<template>
  <div class="filter-grid d-xl-block d-none" :class="panelClass" id="filter-grid">
    <div class="row align-items-center gx-2">
      <template v-for="filter in filters" :key="filter.id">
        <div class="col grid-filter" :key="filter.id" v-if="filter.id !== 'location'">
          <UIMultiSelectDropdown :inputName="filter.inputName" :options="filter.options" :multiSelectLabel="filter.name" :enableAllRule="true" v-if="filter.inputName !== 'format'" @onChanged="handleFilterSelection" ref="filtersRef" :selected="filter.selected" />
          <UIIdSelectDropdown :inputName="filter.inputName" :options="filter.options" :multiSelectLabel="filter.name" :enableAllRule="true" @onChanged="handleFilterSelection" v-if="filter.inputName === 'format'" ref="filtersRef" :selected="filter.selected"  />
        </div>
        <div class="col location-grid-filter" v-if="showLocation && locationCatalog && filter.id === 'location'">
          <LocationSearchForm :showSearchButton="false" :placeholderText="options.locationFilter.placeholder"  @onAutocomplete="handleLocationSearch" :searchQuery="options.locationFilter.selected" :overrideSearch="true" @onFocusout="handleFocusout" ref="locationFilter" />
        </div>      
      </template>
      <div class="col clear-filters text-center">
        <button class="btn btn-link" @click="handleClearFilters">Clear Filters</button>
      </div>
    </div>    
  </div>
</template>

<style lang="scss">
#footer.bottom-sticky-bar  {
  margin-bottom: 60px;
}

.filter-grid {
  border-radius: 12px;
  background-color: $color-accent;
  padding: 10px;
  transition: 0.25s ease top;

  &.sticky {
    position: fixed;
    top: 68px;
    z-index: 101;    
    width: 100%;
    left: 0px;
    border-radius: 0px;
    justify-content: center;
    @include media-breakpoint-up(xl) {
      display: flex !important;
    }

    .row {
      align-self: center;      
      width: 100%;
      @include media-breakpoint-up(xxl) {
        max-width: 1285px;
      }
      @include media-breakpoint-between(xl, xxl) {
        max-width: 1100px;
      }
      
    }
  }

  .grid-filter {
    .input-container {
      margin-top: 0px;      
    }
    .form-floating {
      width: 100%;
    }

    #floatingSelectFormat.form-select {
      height: 50px;
      padding-top: 1.3rem;
      &:focus {
        box-shadow: none;
        border-color: $color-primary;
      }
    }
  }

  .location-grid-filter {
    .location-search-form {
      max-width: 100%;
      @include media-breakpoint-up(sm) {
        margin: 0px auto !important;
      }
      .form-floating label {
        font-size: 14px;
      }

      @include media-breakpoint-down(xxl) {
        .location-suggestions-item span ,
        .location-suggestions-item label {
          font-size: 14px;
        }
      }
    }
  }

  .clear-filters {
    max-width: 105px;
  }
  
}

  
</style>